export const isValidEmail = (email: string): boolean => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

export const getTenDigitPhoneDigits = (phone: string): string =>
  phone.replace(/\D/g, '').replace(/^1(?=\d{10}$)/, '').slice(0, 10);

export const formatPhoneNumber = (phone: string): string => {
  const digits = getTenDigitPhoneDigits(phone);
  if (digits.length <= 3) {
    return digits;
  }
  if (digits.length <= 6) {
    return `${digits.slice(0, 3)}-${digits.slice(3)}`;
  }
  return `${digits.slice(0, 3)}-${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const isTenDigitPhone = (phone: string): boolean =>
  getTenDigitPhoneDigits(phone).length === 10;

export const isValidUrl = (url: string): boolean => {
  if (!url || url.trim() === '') {
return true;
} // Empty URLs are valid (optional field)

  // Auto-prepend https:// if no protocol
  let urlToTest = url.trim();
  if (!urlToTest.match(/^https?:\/\//i)) {
    urlToTest = `https://${  urlToTest}`;
  }

  try {
    const urlObj = new URL(urlToTest);
    // Ensure it has a valid hostname with at least one dot (e.g., example.com)
    return urlObj.hostname.includes('.');
  } catch {
    return false;
  }
};

export const formatUrl = (url: string): string => {
  if (!url || url.trim() === '') {
return '';
}

  let formatted = url.trim();
  // Auto-prepend https:// if no protocol
  if (!formatted.match(/^https?:\/\//i)) {
    formatted = `https://${  formatted}`;
  }

  return formatted;
};

/** `tel:` href for a stored phone, e.g. "+16195550142", or '' when not 10 digits. */
export const telHref = (phone?: string | null): string => {
  const digits = getTenDigitPhoneDigits(phone ?? '');
  return digits.length === 10 ? `+1${digits}` : '';
};

/**
 * A safe href for a portal/web URL, or null when the value isn't a plain web URL.
 * Only http/https render as a link (item 8 acceptance): a scheme-less value is
 * upgraded to https://; any other scheme (mailto:, tel:, javascript:, …) returns
 * null so the caller renders it as plain text instead of an anchor.
 */
export const safePortalUrl = (url?: string | null): string | null => {
  const trimmed = (url ?? '').trim();
  if (!trimmed) {
    return null;
  }
  const schemeMatch = trimmed.match(/^([a-z][a-z0-9+.-]*):/i);
  const candidate = schemeMatch ? trimmed : `https://${trimmed}`;
  if (schemeMatch && !/^https?$/i.test(schemeMatch[1])) {
    return null;
  }
  try {
    const u = new URL(candidate);
    return u.protocol === 'http:' || u.protocol === 'https:' ? candidate : null;
  } catch {
    return null;
  }
};

export const parseOptionalNumber = (value: unknown): number | undefined => {
  if (value === null || value === undefined) {
    return undefined;
  }

  const normalized = String(value).replace(/[$,]/g, '').trim();
  if (!normalized) {
    return undefined;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : undefined;
};

export const getOptionalNumberError = (
  value: unknown,
  label: string,
  options: { min?: number; max?: number } = {}
): string | null => {
  if (value === null || value === undefined) {
    return null;
  }

  const normalized = String(value).replace(/[$,]/g, '').trim();
  if (!normalized) {
    return null;
  }

  const parsed = Number(normalized);
  if (!Number.isFinite(parsed)) {
    return `${label} must be a valid number`;
  }

  if (options.min !== undefined && parsed < options.min) {
    return options.min === 0
      ? `${label} cannot be negative`
      : `${label} must be at least ${options.min}`;
  }

  if (options.max !== undefined && parsed > options.max) {
    return `${label} must be ${options.max} or less`;
  }

  return null;
};

export const formatPhone = (phone: string): string => {
  const cleanPhone = phone.replace(/\D/g, '');
  if (cleanPhone.length === 10) {
    return `(${cleanPhone.slice(0, 3)}) ${cleanPhone.slice(3, 6)}-${cleanPhone.slice(6)}`;
  }
  return phone;
};

export const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(amount);
};
